import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { notifications } from '@mantine/notifications';
import { IconExternalLink } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Coverage, CoverageEligibilityResponse, RelatedPerson } from '@medplum/fhirtypes';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import {
  COVERAGE_INSURANCE_TYPE_CODES,
} from '@unify/fhir-model/constants';
import {
  AuthorizationTrackingSection,
  InsuranceSection,
  VerificationsSection,
  formatMoney,
  formatDate,
  type VerificationEntry,
  type InsuranceEntry,
} from '@/components/billing';
import {
  INSURANCE_TYPE_LABELS,
  coverageToInsuranceEntryExtended,
  coverageDropdownLabel,
  filterActiveCoverages,
  staffCoverageCommandFromInsuranceEntry,
  type InsuranceEntryWithType,
} from '@/lib/billing/coverageInsurance';
import { submitStaffCoverageCommand } from '@/lib/billing/staffCoverageUpdateBot';
import { applyDevManualVobPopulate, DevPopulateManualVobButton } from '@/lib/billing/devManualVobPopulate';
import { submitManualVobEntry, toStaffManualVobPayload } from '@/lib/eligibilityBotService';
import { isEligiblePrimaryCoverage } from '@unify/fhir-model/resources/coverage';
import { formatDateOnlyLocal } from '@/lib/dateHelpers';
import {
  defaultManualVobFormValues,
  manualVobSchema,
  type ManualVobFormInput,
} from '@/schemas/manual-vob.schema';
import { ManualVobBenefitFields } from '@/components/billing/ManualVobBenefitFields';
import {
  buildInsuranceAuthorizationBasic,
  type InsuranceAuthorizationRecord,
} from '@/lib/billing/insuranceAuthorization';
import {
  buildInsuranceCardDocumentReference,
  currentInsuranceCards,
  type InsuranceCardDocument,
} from '@/lib/billing/insuranceCards';
import { createSecuredAttachment } from '@/lib/createSecuredAttachment';
import { buildBillingTimeline } from '@/lib/billing/billingTimeline';
import type { BillingTimelineItem } from '@/lib/billing/billingTimelineTypes';
import {
  cerToVerificationHistoryEntry,
  sortVerificationHistory,
  verificationHistoryStatusColor,
  verificationHistoryStatusLabel,
  type VerificationHistoryEntry,
} from '@/lib/billing/verificationHistory';
import type { ChargeItem, Encounter, Invoice } from '@medplum/fhirtypes';

// ---------------------------------------------------------------------------
// Insurance coverage (live FHIR)
// ---------------------------------------------------------------------------

export function ClientProfileInsurancePanel({
  patientId,
  coverages,
  relatedPersons = [],
  insuranceCards,
  onSaved,
}: {
  patientId?: string;
  patientDisplay?: string;
  coverages: Coverage[];
  relatedPersons?: RelatedPerson[];
  insuranceCards: InsuranceCardDocument[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const medplum = useMedplum();
  const patientRef = patientId ? `Patient/${patientId}` : '';
  const activeCoverages = useMemo(() => filterActiveCoverages(coverages), [coverages]);
  const insurances = useMemo(
    () => activeCoverages.map((c) => coverageToInsuranceEntryExtended(c, relatedPersons)),
    [activeCoverages, relatedPersons],
  );

  const uploadCard = async (
    coverageId: string,
    side: 'front' | 'back',
    file: File,
  ) => {
    const coverageRef = `Coverage/${coverageId}`;
    const existing = currentInsuranceCards(
      insuranceCards.filter((c) => c.coverageRef === `Coverage/${coverageId}`),
    );
    const supersede = side === 'front' ? existing.front : existing.back;
    const draft = await medplum.createResource(
      buildInsuranceCardDocumentReference({
        patientRef,
        coverageRef,
        side,
        fileName: file.name,
        contentType: file.type,
        supersedeId: supersede?.id,
      }),
    );
    try {
      const attachment = await createSecuredAttachment(
        medplum,
        file,
        { reference: `DocumentReference/${draft.id}` },
        file.name,
        file.type,
      );
      await medplum.updateResource({
        ...draft,
        docStatus: 'final',
        content: [{ attachment }],
      });
    } catch (error) {
      await medplum.updateResource({ ...draft, status: 'entered-in-error' });
      throw error;
    }
    if (supersede?.id) {
      const old = await medplum.readResource('DocumentReference', supersede.id);
      await medplum.updateResource({ ...old, status: 'superseded' });
    }
  };

  const handleSaveInsurance = async (
    draft: InsuranceEntry,
    context: {
      mode: 'add' | 'edit';
      editingId: string | null;
      cardFiles: { front?: File; back?: File };
    },
  ) => {
    if (!patientId) return;

    const command = staffCoverageCommandFromInsuranceEntry(
      patientId,
      draft as InsuranceEntryWithType,
      context.mode,
    );
    const saved = await submitStaffCoverageCommand(medplum, command);
    const coverageId = saved.coverageId;

    if (coverageId) {
      if (context.cardFiles.front) {
        await uploadCard(coverageId, 'front', context.cardFiles.front);
      }
      if (context.cardFiles.back) {
        await uploadCard(coverageId, 'back', context.cardFiles.back);
      }
    }

    medplum.invalidateSearches('Coverage');
    medplum.invalidateSearches('DocumentReference');
    await onSaved();
  };

  const handleRemove = async (removedId: string) => {
    const cov = coverages.find((c) => c.id === removedId);
    if (!cov?.id || !patientId || !cov.meta?.versionId) {
      throw new Error('Coverage not found');
    }
    await submitStaffCoverageCommand(medplum, {
      action: 'cancel',
      patientId,
      coverageId: cov.id,
      expectedVersionId: cov.meta.versionId,
    });
    medplum.invalidateSearches('Coverage');
    await onSaved();
  };

  return (
    <InsuranceSection
      insurances={insurances}
      onInsurancesChange={() => {}}
      insuranceTypeOptions={COVERAGE_INSURANCE_TYPE_CODES.map((code) => ({
        value: code,
        label: INSURANCE_TYPE_LABELS[code],
      }))}
      insuranceCards={insuranceCards}
      onSaveInsurance={handleSaveInsurance}
      onRemoveInsurance={handleRemove}
    />
  );
}

// ---------------------------------------------------------------------------
// Verification history
// ---------------------------------------------------------------------------

export function ClientProfileVerificationPanel({
  coverages,
  eligibilityResponses,
  onSaved,
}: {
  coverages: Coverage[];
  eligibilityResponses: CoverageEligibilityResponse[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const medplum = useMedplum();
  const [vobOpen, setVobOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const today = formatDateOnlyLocal(new Date());
  const eligibleCoverages = useMemo(
    () => filterActiveCoverages(coverages).filter((c) => isEligiblePrimaryCoverage(c, today)),
    [coverages, today],
  );
  const insurances = useMemo(
    () => eligibleCoverages.map((c) => coverageToInsuranceEntryExtended(c)),
    [eligibleCoverages],
  );
  const history = useMemo(
    () => sortVerificationHistory(
      eligibilityResponses
        .map((cer) => cerToVerificationHistoryEntry(cer, coverages))
        .filter((v): v is VerificationHistoryEntry => v != null),
    ),
    [eligibilityResponses, coverages],
  );

  const verifications: VerificationEntry[] = history;
  const form = useForm<ManualVobFormInput>({
    initialValues: defaultManualVobFormValues,
    validate: zod4Resolver(manualVobSchema),
  });

  const historyColumns = useMemo((): UnifyTableColumn<VerificationHistoryEntry>[] => [
    { key: 'date', label: 'Date', render: (v) => <Text size="sm">{v.verifiedAt ? formatDate(v.verifiedAt) : '—'}</Text>, getValue: (v) => v.verifiedAt ?? '' },
    { key: 'provider', label: 'Payer', render: (v) => <Text size="sm" fw={600}>{v.provider}</Text>, getValue: (v) => v.provider },
    { key: 'code', label: 'Code', render: (v) => <Text size="sm">{v.billingCode ?? '—'}</Text>, getValue: (v) => v.billingCode ?? '' },
    { key: 'status', label: 'Status', render: (v) => <Badge color={verificationHistoryStatusColor(v.historyStatus)} variant="light">{verificationHistoryStatusLabel(v.historyStatus)}</Badge>, getValue: (v) => v.historyStatus },
    { key: 'inn', label: 'INN copay', render: (v) => <Text size="sm">{v.innCopayCents != null ? formatMoney(v.innCopayCents) : '—'}</Text>, getValue: (v) => String(v.innCopayCents ?? '') },
    { key: 'oon', label: 'OON copay', render: (v) => <Text size="sm">{v.oonCopayCents != null ? formatMoney(v.oonCopayCents) : '—'}</Text>, getValue: (v) => String(v.oonCopayCents ?? '') },
    { key: 'verifiedBy', label: 'Verified by', render: (v) => <Text size="sm">{v.verifiedBy ?? '—'}</Text>, getValue: (v) => v.verifiedBy ?? '' },
    { key: 'note', label: 'Notes', render: (v) => <Text size="sm" lineClamp={2}>{v.payerMessage ?? '—'}</Text>, getValue: (v) => v.payerMessage ?? '' },
  ], []);

  const openManual = (): void => {
    form.setValues({
      ...defaultManualVobFormValues,
      coverageId: eligibleCoverages[0]?.id ?? '',
      serviceDate: today,
    });
    setSubmitError(null);
    setVobOpen(true);
  };

  const handleSubmit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitManualVobEntry(medplum, toStaffManualVobPayload(values, { serviceDate: today }));
      if (!result.ok) {
        setSubmitError(result.reason ?? 'Manual entry failed');
        return;
      }
      medplum.invalidateSearches('CoverageEligibilityResponse');
      setVobOpen(false);
      await onSaved();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <>
      <VerificationsSection
        insurances={insurances}
        verifications={verifications}
        onVerificationsChange={() => {}}
        onAddManualVerification={eligibleCoverages.length > 0 ? openManual : undefined}
        historySlot={(
          <>
            <Title order={5} mb="md">Verification history</Title>
            {history.length === 0 ? (
              <Text size="sm" c="gray.6">No verification history.</Text>
            ) : (
              <UnifyTable withPaper={false} columns={historyColumns} data={history} getRowKey={(v) => v.id} />
            )}
          </>
        )}
      />
      <Modal
        opened={vobOpen}
        onClose={() => setVobOpen(false)}
        title={
          <Group justify="space-between" wrap="nowrap">
            <Text fw={700}>Manual eligibility (VOB)</Text>
            <DevPopulateManualVobButton
              onPopulate={() => form.setValues(applyDevManualVobPopulate(form.values))}
            />
          </Group>
        }
        styles={{ title: { flex: 1 } }}
        size="xl"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Select
              label="Coverage"
              data={eligibleCoverages.map((c) => ({
                value: c.id ?? '',
                label: coverageDropdownLabel(c),
              }))}
              {...form.getInputProps('coverageId')}
            />
            <Select
              label="Billing service code"
              data={[
                { value: 'PSY', label: 'PSY' },
                { value: 'IOP', label: 'IOP' },
                { value: 'PCH', label: 'PCH' },
              ]}
              {...form.getInputProps('billingCode')}
            />
            <Switch
              label="Coverage in force / eligible"
              checked={form.values.inforce}
              onChange={(e) => form.setFieldValue('inforce', e.currentTarget.checked)}
            />
            <ManualVobBenefitFields form={form} />
            <Textarea
              placeholder="Disposition or notes"
              autosize
              minRows={2}
              {...form.getInputProps('disposition')}
            />
            {submitError && <Alert color="red">{submitError}</Alert>}
            <Group justify="flex-end">
              <Button variant="subtle" type="button" onClick={() => setVobOpen(false)}>Cancel</Button>
              <Button type="submit" loading={submitting} disabled={!form.values.coverageId}>Save benefits</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Authorization tracking
// ---------------------------------------------------------------------------

export function ClientProfileAuthorizationPanel({
  patientId,
  coverages,
  authorizations,
  onSaved,
}: {
  patientId?: string;
  coverages: Coverage[];
  authorizations: InsuranceAuthorizationRecord[];
  onSaved: () => Promise<void>;
}): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);
  const patientRef = patientId ? `Patient/${patientId}` : '';
  const activeCoverages = useMemo(() => filterActiveCoverages(coverages), [coverages]);
  const defaultCoverageRef = activeCoverages[0]?.id ? `Coverage/${activeCoverages[0].id}` : undefined;

  const saveAuthorization = async (draft: InsuranceAuthorizationRecord) => {
    if (!patientRef) return;
    setSaving(true);
    try {
      const resource = buildInsuranceAuthorizationBasic({
        patientRef,
        coverageRef: draft.coverageRef ?? defaultCoverageRef,
        authorizationNumber: draft.authorizationNumber,
        validFrom: draft.validFrom,
        validThrough: draft.validThrough,
        serviceType: draft.serviceType,
        limitType: draft.limitType,
        authorizedUses: draft.authorizedUses,
        usedCount: draft.usedCount,
        reminderThreshold: draft.reminderThreshold,
        remindMonthBeforeExpiring: draft.remindMonthBeforeExpiring,
        manualInactive: draft.manualInactive,
        notes: draft.notes,
      });
      if (draft.id) {
        await medplum.updateResource({ ...resource, id: draft.id });
      } else {
        await medplum.createResource(resource);
      }
      medplum.invalidateSearches('Basic');
      await onSaved();
    } catch {
      notifications.show({ color: 'red', title: 'Save failed', message: 'Could not save authorization.' });
      throw new Error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteAuthorization = async (id: string) => {
    try {
      await medplum.deleteResource('Basic', id);
      medplum.invalidateSearches('Basic');
      await onSaved();
      notifications.show({ color: 'green', title: 'Removed', message: 'Authorization removed.' });
    } catch {
      notifications.show({ color: 'red', title: 'Delete failed', message: 'Could not remove authorization.' });
      throw new Error('Delete failed');
    }
  };

  return (
    <AuthorizationTrackingSection
      authorizations={authorizations}
      patientRef={patientRef}
      defaultCoverageRef={defaultCoverageRef}
      saving={saving}
      onSave={saveAuthorization}
      onDelete={deleteAuthorization}
    />
  );
}

// ---------------------------------------------------------------------------
// Billing history
// ---------------------------------------------------------------------------

export function ClientProfileBillingHistoryPanel({
  encounters,
  chargeItems,
  invoices,
}: {
  encounters: Encounter[];
  chargeItems: ChargeItem[];
  invoices: Invoice[];
}): JSX.Element {
  const timeline = useMemo(
    () => buildBillingTimeline({ encounters, chargeItems, invoices }),
    [encounters, chargeItems, invoices],
  );

  const columns = useMemo((): UnifyTableColumn<BillingTimelineItem>[] => [
    { key: 'date', label: 'Date', render: (r) => <Text size="sm">{r.dateOfService ? formatDate(r.dateOfService) : '—'}</Text>, getValue: (r) => r.dateOfService ?? '' },
    { key: 'service', label: 'Service', render: (r) => <Text size="sm" fw={600}>{r.serviceLabel ?? r.serviceType ?? '—'}</Text>, getValue: (r) => r.serviceLabel ?? '' },
    { key: 'provider', label: 'Provider', render: (r) => <Text size="sm">{r.providerName ?? '—'}</Text>, getValue: (r) => r.providerName ?? '' },
    { key: 'cpt', label: 'CPT', render: (r) => <Text size="sm">{r.cptCode ?? '—'}</Text>, getValue: (r) => r.cptCode ?? '' },
    { key: 'client', label: 'Client resp.', render: (r) => <Text size="sm">{r.clientResponsibility != null ? formatMoney(r.clientResponsibility) : '—'}</Text>, getValue: (r) => String(r.clientResponsibility ?? '') },
    { key: 'paid', label: 'Copay/paid', render: (r) => <Text size="sm">{r.clientPaid != null ? formatMoney(r.clientPaid) : '—'}</Text>, getValue: (r) => String(r.clientPaid ?? '') },
    { key: 'claim', label: 'Claim', render: (r) => <Text size="sm">{r.claimStatus ?? r.clientVisibleStatus}</Text>, getValue: (r) => r.claimStatus ?? r.clientVisibleStatus },
    { key: 'link', label: '', sortable: false, render: (r) => r.sourceHref ? (
      <Button component={Link} to={r.sourceHref} size="xs" variant="subtle" leftSection={<IconExternalLink size={14} />}>Open</Button>
    ) : null, getValue: () => '' },
  ], []);

  return (
    <Card withBorder>
      <Title order={4} mb="md">Billing & claims history</Title>
      {timeline.length === 0 ? (
        <Text size="sm" c="gray.6">No billing history.</Text>
      ) : (
        <UnifyTable withPaper={false} columns={columns} data={timeline} getRowKey={(r) => r.id} />
      )}
    </Card>
  );
}
